import { UtilityTargetSelector } from "@/shared/selectors/UtilityTargetSelector";
import { pageBgColor } from "@/utils/theme";
import { HelmetDexteel, useHasPermission } from "@dexteel/mesf-core";
import { DashboardOutlined, TableChartOutlined } from "@mui/icons-material";
import {
	Box,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import {
	parseAsArrayOf,
	parseAsString,
	parseAsStringLiteral,
	useQueryState,
	useQueryStates,
} from "nuqs";
import { useEffect } from "react";
import { UtilityMessage } from "../shared/components/UtilityStates";
import { UtilityTabs } from "../shared/components/UtilityTabs";
import { ConsumptionControls } from "../shared/consumption/components/ConsumptionControls";
import { ConsumptionFilterBar } from "../shared/consumption/components/ConsumptionFilterBar";
import { ConsumptionReport } from "../shared/consumption/components/ConsumptionReport";
import {
	ConsumptionError,
	ConsumptionSkeletons,
} from "../shared/consumption/components/ConsumptionStates";
import { DataThroughCaption } from "../shared/consumption/components/DataThroughCaption";
import { useGasConsumptionZones } from "../shared/consumption/hooks/useGasConsumptionZones";
import { useUtilityConsumption } from "../shared/consumption/hooks/useUtilityConsumption";
import { useUtilityConsumptionMeters } from "../shared/consumption/hooks/useUtilityConsumptionMeters";
import {
	UTILITY_CONSUMPTION_MODE,
	UTILITY_CONSUMPTION_VIEW,
	type UtilityConsumptionMode,
	type UtilityConsumptionReportRange,
	type UtilityConsumptionView,
} from "../shared/consumption/models/UtilityConsumption";
import {
	fromUtilityConsumptionDateKey,
	getCurrentUtilityConsumptionPeriod,
	getDefaultUtilityConsumptionReportRange,
	getUtilityConsumptionPeriod,
	toUtilityConsumptionDateKey,
} from "../shared/consumption/utils/calendar";
import { parseAsUtilityConsumptionMode } from "../shared/consumption/utils/queryParsers";
import { useUtilityKind } from "../shared/hooks/useUtilityKind";
import { UTILITY_KIND, type UtilityKind } from "../shared/models/Utility";
import { AtAGlance } from "./components/AtAGlance";
import { ConsumptionChart } from "./components/ConsumptionChart";
import { GasDashboard } from "./components/gas/GasDashboard";
import { GasReport } from "./components/gas/GasReport";
import { useUtilityTargetSelection } from "./hooks/useUtilityTargetSelection";

const CONSUMPTION_UTILITIES = [UTILITY_KIND.WATER, UTILITY_KIND.GAS];

const CONSUMPTION_QUERY = {
	view: parseAsStringLiteral(
		Object.values(UTILITY_CONSUMPTION_VIEW),
	).withDefault(UTILITY_CONSUMPTION_VIEW.DASHBOARD),
	chart: parseAsUtilityConsumptionMode.withDefault(
		UTILITY_CONSUMPTION_MODE.DAY,
	),
	date: parseAsString,
	from: parseAsString,
	to: parseAsString,
};

export const ConsumptionPage = () => {
	const hasPermissionTo = useHasPermission();
	const canSelectArea = hasPermissionTo("ui.mill.consumption.area.select");
	const [utility, setUtilityKind] = useUtilityKind(CONSUMPTION_UTILITIES);
	const isGas = utility === UTILITY_KIND.GAS;

	const { targetId: selectedTargetId, setTargetId: setSelectedTargetId } =
		useUtilityTargetSelection({ millsOnly: isGas });

	const [meterIds, setMeterIds] = useQueryState(
		"meters",
		parseAsArrayOf(parseAsString),
	);
	const [{ view, chart: mode, date, from, to }, setQuery] =
		useQueryStates(CONSUMPTION_QUERY);

	const referenceDate = fromUtilityConsumptionDateKey(
		date,
		() => getCurrentUtilityConsumptionPeriod(mode).startUtc,
	);

	const period = getUtilityConsumptionPeriod(mode, referenceDate);
	const defaultReportRange = getDefaultUtilityConsumptionReportRange();
	const reportRange = {
		startDateKey: from ?? defaultReportRange.startDateKey,
		endDateKey: to ?? defaultReportRange.endDateKey,
	};
	const setReportRange = (range: UtilityConsumptionReportRange) =>
		setQuery({ from: range.startDateKey, to: range.endDateKey });
	const setView = (nextView: UtilityConsumptionView) =>
		setQuery({ view: nextView });

	const areaSelector = canSelectArea ? (
		<UtilityTargetSelector
			targetId={selectedTargetId}
			millsOnly={isGas}
			onTargetChange={(targetId) => setSelectedTargetId(targetId)}
		/>
	) : undefined;

	const {
		data: meters,
		error: meterError,
		isError: isMeterError,
		isLoading: isMeterLoading,
		refetch: refetchMeters,
		resolvedExternalMeterIds,
		isEmptySelection,
	} = useUtilityConsumptionMeters({
		targetId: selectedTargetId,
		enabled: !isGas,
		externalMeterIds: meterIds,
	});

	const zoneSelection = useGasConsumptionZones({
		targetId: selectedTargetId,
		enabled: isGas,
		includeTotals: view === UTILITY_CONSUMPTION_VIEW.DASHBOARD,
	});

	const { data, error, isError, isLoading, refetch } = useUtilityConsumption({
		targetId: selectedTargetId,
		enabled: view === UTILITY_CONSUMPTION_VIEW.DASHBOARD && !isEmptySelection,
		utility,
		externalMeterIds: resolvedExternalMeterIds,
		mode,
		period,
	});

	const showCost = selectedTargetId !== null;
	const setUtility = (next: UtilityKind) => {
		setMeterIds(null);
		zoneSelection.changeZoneKeys(null);
		setUtilityKind(next);
	};

	const setPeriodMode = (nextMode: UtilityConsumptionMode) =>
		setQuery({ chart: nextMode, date: null });
	const setReferenceDate = (value: Date) =>
		setQuery({ date: toUtilityConsumptionDateKey(value) });
	const filterByMeter = (meterId: string) =>
		setMeterIds((current) =>
			current !== null && current.length === 1 && current[0] === meterId
				? null
				: [meterId],
		);

	useEffect(() => {
		if (meterIds === null || meters === undefined) return;
		const configuredIds = new Set(meters.map((meter) => meter.ExternalMeterId));
		const validIds = meterIds.filter((meterId) => configuredIds.has(meterId));
		if (validIds.length === meterIds.length) return;
		setMeterIds(validIds.length === 0 ? null : validIds);
	}, [meterIds, meters]);

	return (
		<>
			<HelmetDexteel title="Utility Report" />
			<Box
				sx={{
					bgcolor: pageBgColor,
					boxSizing: "border-box",
					display: "flex",
					flexDirection: "column",
					height: { lg: "calc(100vh - 90px)" },
					minHeight: { lg: "calc(100vh - 90px)" },
					p: { xs: 1, md: 2 },
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexWrap: "wrap",
						justifyContent: "space-between",
						alignItems: "center",
						gap: 1,
						mb: 1,
					}}
				>
					<Typography
						component="h1"
						sx={{ fontSize: "1.5rem", fontWeight: 600, lineHeight: 1.25 }}
					>
						Utility Report
					</Typography>
					<Box
						sx={{
							display: "flex",
							gap: 0.75,
							alignItems: "center",
							flexWrap: "wrap",
						}}
					>
						<ToggleButtonGroup
							exclusive
							size="small"
							value={view}
							aria-label="Consumption view"
							onChange={(_, value: UtilityConsumptionView | null) =>
								value && setView(value)
							}
							sx={{
								"& .MuiToggleButton-root": { px: 1.25, textTransform: "none" },
								"& .Mui-selected": {
									bgcolor: "primary.main",
									color: "primary.contrastText",
									"&:hover": { bgcolor: "primary.dark" },
								},
							}}
						>
							<Tooltip title="Dashboard view: cards and charts">
								<ToggleButton
									value={UTILITY_CONSUMPTION_VIEW.DASHBOARD}
									aria-label="Dashboard view"
								>
									<DashboardOutlined fontSize="small" sx={{ mr: 0.5 }} />
									Dashboard
								</ToggleButton>
							</Tooltip>
							<Tooltip title="Report view: detailed table">
								<ToggleButton
									value={UTILITY_CONSUMPTION_VIEW.REPORT}
									aria-label="Report view"
								>
									<TableChartOutlined fontSize="small" sx={{ mr: 0.5 }} />
									Report
								</ToggleButton>
							</Tooltip>
						</ToggleButtonGroup>
					</Box>
				</Box>
				<UtilityTabs
					utility={utility}
					available={CONSUMPTION_UTILITIES}
					onUtilityChange={setUtility}
				/>
				{isMeterError ? (
					<ConsumptionError
						message={
							meterError instanceof Error
								? meterError.message
								: "Unexpected response."
						}
						onRetry={() => refetchMeters()}
					/>
				) : !canSelectArea ? (
					<>
						<ConsumptionFilterBar
							meters={[]}
							isMeterLoading={false}
							selectedMeterIds={null}
							areaSelector={areaSelector}
							onMeterChange={setMeterIds}
						/>
						<UtilityMessage
							title="Area not supported"
							detail="This area has no utility target of its own, so its consumption is not tracked."
						/>
					</>
				) : view === UTILITY_CONSUMPTION_VIEW.DASHBOARD && isGas ? (
					<GasDashboard
						targetId={selectedTargetId}
						zoneSelection={zoneSelection}
						areaSelector={areaSelector}
						mode={mode}
						period={period}
						controls={
							<ConsumptionControls
								mode={mode}
								referenceDate={period.startUtc}
								onModeChange={setPeriodMode}
								onReferenceDateChange={setReferenceDate}
							/>
						}
					/>
				) : view === UTILITY_CONSUMPTION_VIEW.DASHBOARD ? (
					<>
						<ConsumptionFilterBar
							meters={meters ?? []}
							isMeterLoading={isMeterLoading}
							selectedMeterIds={meterIds}
							areaSelector={areaSelector}
							onMeterChange={setMeterIds}
						/>
						{isEmptySelection ? (
							<UtilityMessage
								title="No meters selected"
								detail="Tick at least one meter in the filter to see consumption."
							/>
						) : (
							<Box
								sx={{
									display: "flex",
									flex: 1,
									flexDirection: "column",
									gap: 1,
									minHeight: 0,
								}}
							>
								{isError && (
									<ConsumptionError
										message={
											error instanceof Error
												? error.message
												: "Unexpected response."
										}
										onRetry={() => refetch()}
									/>
								)}
								{isLoading && data === undefined && <ConsumptionSkeletons />}
								{data && meters?.length === 0 && (
									<UtilityMessage
										title="No meters configured"
										detail="No active water meters are assigned to this mill."
									/>
								)}
								{data &&
									(meters?.length ?? 0) > 0 &&
									!data.summary.IsAvailable && (
										<UtilityMessage
											title={
												meterIds !== null
													? "Selected meters have no readings"
													: "No reporting meters"
											}
										/>
									)}
								{data?.summary.IsAvailable && (
									<>
										<DataThroughCaption
											dataThroughUtc={data.summary.DataThroughUtc}
										/>
										<AtAGlance
											summary={data.summary}
											millRolling={data.millRolling}
											meterGaps={data.meterGaps}
											showCost={showCost}
											buckets={data.miniBuckets}
										/>
										<ConsumptionChart
											buckets={data.mainBuckets}
											meters={meters ?? []}
											summary={data.summary}
											mode={mode}
											period={period}
											selectedMeterIds={resolvedExternalMeterIds}
											onMeterFilter={filterByMeter}
											controls={
												<ConsumptionControls
													mode={mode}
													referenceDate={period.startUtc}
													onModeChange={setPeriodMode}
													onReferenceDateChange={setReferenceDate}
												/>
											}
										/>
									</>
								)}
							</Box>
						)}
					</>
				) : isGas ? (
					<GasReport
						targetId={selectedTargetId}
						zoneSelection={zoneSelection}
						areaSelector={areaSelector}
						range={reportRange}
						onRangeChange={setReportRange}
					/>
				) : (
					<ConsumptionReport
						targetId={selectedTargetId}
						utility={utility}
						meters={meters ?? []}
						isMeterLoading={isMeterLoading}
						selectedMeterIds={meterIds}
						resolvedMeterIds={resolvedExternalMeterIds}
						areMetersLoaded={meters !== undefined}
						isEmptySelection={isEmptySelection}
						range={reportRange}
						areaSelector={areaSelector}
						showCost={showCost}
						onMeterChange={setMeterIds}
						onRangeChange={setReportRange}
					/>
				)}
			</Box>
		</>
	);
};
